'use client';

import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import { useIsomorphicLayoutEffect } from '@common/hooks';
import type SvgMyFill from '@common/icon/SvgMyFill';
import { cn } from '@core/utils';
import { useLinkStatus } from 'next/link';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { useHideOnScroll } from '@/app/_components/SearchBar';
import { DONATION_ROUTES } from '@/constants';
import { useHaptic } from '@/hooks/useHaptic';
import { useRenderedPathname } from '@/hooks/useRenderedPathname';
import { scrollToPageTop } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';
const TRANSITION_MS = 300;
//* 크기는 CSS 전이로 실제 레이아웃을 바꾸고, 활성 배경은 JS가 같은 곡선(easeOutCubic)으로 탭을 따라간다
const TRANSITION_CLASS_NAME = 'duration-300 ease-[cubic-bezier(0.33,1,0.68,1)] motion-reduce:transition-none';
const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;
interface IndicatorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}
const interpolateRect = (from: IndicatorRect, to: IndicatorRect, progress: number): IndicatorRect => ({
  left: from.left + (to.left - from.left) * progress,
  top: from.top + (to.top - from.top) * progress,
  width: from.width + (to.width - from.width) * progress,
  height: from.height + (to.height - from.height) * progress
});
export interface FloatingBnbItem {
  label: string;
  pathname: string;
  //* 활성 판정 경로와 이동 주소가 다를 때만 쓴다
  href?: string;
  icon: typeof SvgMyFill;
  hasWideGlyph?: boolean;
}
const isItemActive = (itemPathname: string, pathname: string | null) => {
  if (!pathname) {
    return false;
  }

  //* 홈 경로는 모든 기부 경로의 앞부분이라 정확히 같을 때만 활성이다
  if (itemPathname === DONATION_ROUTES.HOME.pathname) {
    return pathname === itemPathname;
  }
  return pathname === itemPathname || pathname.startsWith(`${itemPathname}/`);
};
interface FloatingBnbTabContentProps extends Omit<FloatingBnbItem, 'pathname'> {
  isActive: boolean;
  isCompact: boolean;
}
const FloatingBnbTabContent = t0 => {
  const $ = _c(34);
  const {
    label,
    icon: Icon,
    hasWideGlyph,
    isActive,
    isCompact
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const tabRef = useRef(null);
  const isWideGlyphShown = hasWideGlyph && !isActive;
  const isLabelShown = isActive && !isCompact;
  let t1;
  let t2;
  if ($[0] !== pending) {
    t1 = () => {
      if (!pending) {
        return;
      }
      const linkElement = tabRef.current?.closest("a");
      if (!linkElement) {
        return;
      }
      const preventRepeatedNavigation = _temp;
      linkElement.setAttribute("aria-busy", "true");
      linkElement.setAttribute("aria-disabled", "true");
      linkElement.addEventListener("click", preventRepeatedNavigation, true);
      return () => {
        linkElement.removeAttribute("aria-busy");
        linkElement.removeAttribute("aria-disabled");
        linkElement.removeEventListener("click", preventRepeatedNavigation, true);
      };
    };
    t2 = [pending];
    $[0] = pending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = isCompact ? "h-34" : isActive ? "h-42" : "h-38";
  const t4 = isWideGlyphShown ? "px-9" : isCompact ? "px-13" : "px-10";
  const t5 = isLabelShown && "min-w-68";
  let t6;
  if ($[3] !== t3 || $[4] !== t4 || $[5] !== t5) {
    t6 = cn("relative flex items-center justify-center transition-[height,padding,min-width]", TRANSITION_CLASS_NAME, t3, t4, t5);
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = isWideGlyphShown || !isCompact ? "size-26" : "size-22";
  const t8 = pending && "animate-pulse opacity-60 motion-reduce:animate-none";
  let t9;
  if ($[7] !== t7 || $[8] !== t8) {
    t9 = cn("block transition-[width,height,opacity]", TRANSITION_CLASS_NAME, t7, t8);
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = isActive ? "text-O-500" : "text-Gray-white";
  let t11;
  if ($[10] !== t10) {
    t11 = cn("size-full transition-colors duration-150", t10);
    $[10] = t10;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== Icon || $[13] !== t11) {
    t12 = <Icon className={t11} />;
    $[12] = Icon;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t12 || $[16] !== t9) {
    t13 = <span className={t9}>{t12}</span>;
    $[15] = t12;
    $[16] = t9;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = isLabelShown ? "grid-cols-[1fr] opacity-100" : "grid-cols-[0fr] opacity-0";
  let t15;
  if ($[18] !== t14) {
    t15 = cn("grid transition-[grid-template-columns,opacity]", TRANSITION_CLASS_NAME, t14);
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const t16 = isLabelShown ? undefined : true;
  let t17;
  if ($[20] !== label || $[21] !== t16) {
    t17 = <span className="overflow-hidden"><span aria-hidden={t16} className="block whitespace-nowrap pl-4 font-600 text-O-500 typo-caption1">{label}</span></span>;
    $[20] = label;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t15 || $[24] !== t17) {
    t18 = <span className={t15}>{t17}</span>;
    $[23] = t15;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = pending ? "\uD398\uC774\uC9C0 \uC774\uB3D9 \uC911" : null;
  let t20;
  if ($[26] !== t19) {
    t20 = <span aria-live="polite" className="sr-only" role="status">{t19}</span>;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== isActive || $[29] !== t13 || $[30] !== t18 || $[31] !== t20 || $[32] !== t6) {
    t21 = <span ref={tabRef} data-floating-bnb-active={isActive} className={t6}>{t13}{t18}{t20}</span>;
    $[28] = isActive;
    $[29] = t13;
    $[30] = t18;
    $[31] = t20;
    $[32] = t6;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  return t21;
};
interface FloatingBnbProps {
  items: readonly FloatingBnbItem[];
  leading?: ReactNode;
}

//* 연말 이벤트 한정 플로팅 BNB — 되돌리는 절차는 src/plans/WEL-5478-donation-home-entry.md
export const FloatingBnb = t0 => {
  const $ = _c(27);
  const {
    items,
    leading
  } = t0;
  const pathname = useRenderedPathname();
  const {
    triggerHaptic
  } = useHaptic();
  const {
    hidden: isCompact
  } = useHideOnScroll();
  const listRef = useRef(null);
  const indicatorRef = useRef(null);
  const paintedRectRef = useRef(null);
  let t1;
  if ($[0] !== items || $[1] !== pathname) {
    let t2;
    if ($[3] !== pathname) {
      t2 = item => isItemActive(item.pathname, pathname);
      $[3] = pathname;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = items.findIndex(t2);
    $[0] = items;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeIndex = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const list = listRef.current;
      const indicator = indicatorRef.current;
      if (!list || !indicator) {
        return;
      }
      const startRect = paintedRectRef.current;
      const isReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const startTime = performance.now();
      let frameId = 0;
      const paint = now => {
        const activeTab = list.querySelector("[data-floating-bnb-active=\"true\"]");
        if (!activeTab) {
          indicator.style.opacity = "0";
          paintedRectRef.current = null;
          return;
        }
        const targetRect = {
          left: activeTab.offsetLeft,
          top: activeTab.offsetTop,
          width: activeTab.offsetWidth,
          height: activeTab.offsetHeight
        };
        const progress = isReducedMotion || !startRect ? 1 : Math.min(Math.max((now - startTime) / TRANSITION_MS, 0), 1);
        const rect = startRect ? interpolateRect(startRect, targetRect, easeOutCubic(progress)) : targetRect;
        indicator.style.opacity = "1";
        indicator.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
        indicator.style.width = `${rect.width}px`;
        indicator.style.height = `${rect.height}px`;
        paintedRectRef.current = rect;
        if (!isReducedMotion && now - startTime < TRANSITION_MS + 50) {
          frameId = requestAnimationFrame(paint);
        }
      };
      paint(startTime);
      return () => {
        cancelAnimationFrame(frameId);
      };
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== activeIndex || $[7] !== isCompact) {
    t3 = [activeIndex, isCompact];
    $[6] = activeIndex;
    $[7] = isCompact;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useIsomorphicLayoutEffect(t2, t3);
  const t4 = isCompact ? "w-226 px-4" : "w-260 px-6";
  let t5;
  if ($[9] !== t4) {
    t5 = cn("pointer-events-auto flex items-center gap-4 rounded-pill border border-Gray-black/5 bg-Gray-black/50 py-4 transition-[padding,width]", TRANSITION_CLASS_NAME, t4);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span ref={indicatorRef} aria-hidden={true} className="pointer-events-none absolute left-0 top-0 rounded-pill bg-O-25 opacity-0 transition-opacity duration-150" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isCompact || $[13] !== items || $[14] !== pathname || $[15] !== triggerHaptic) {
    let t8;
    if ($[17] !== isCompact || $[18] !== pathname || $[19] !== triggerHaptic) {
      t8 = t9 => {
        const {
          label,
          pathname: itemPathname,
          href,
          icon,
          hasWideGlyph
        } = t9;
        const isCurrentPage = pathname === itemPathname;
        return <li key={label}><CustomLink href={href ?? itemPathname} aria-current={isCurrentPage ? "page" : undefined} aria-label={`${label} 페이지로 이동`} className={cn("block rounded-pill transition-opacity duration-150 hover:opacity-80 active:opacity-80 motion-reduce:transition-none", FOCUS_RING)} onClick={event => {
            if (isCurrentPage) {
              event.preventDefault();
              scrollToPageTop();
              return;
            }
            triggerHaptic();
          }} {...gtmDatasets.clickFnb({
            menu_name: label
          })}><FloatingBnbTabContent label={label} icon={icon} hasWideGlyph={hasWideGlyph} isActive={isItemActive(itemPathname, pathname)} isCompact={isCompact} /></CustomLink></li>;
      };
      $[17] = isCompact;
      $[18] = pathname;
      $[19] = triggerHaptic;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    t7 = items.map(t8);
    $[12] = isCompact;
    $[13] = items;
    $[14] = pathname;
    $[15] = triggerHaptic;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <div ref={listRef} className="relative flex-1">{t6}<ul className="flex items-center justify-between">{t7}</ul></div>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== leading || $[24] !== t5 || $[25] !== t8) {
    t9 = <nav aria-label={"\uAE30\uBD80 \uB0B4\uBE44\uAC8C\uC774\uC158"} className={t5}>{leading}{t8}</nav>;
    $[23] = leading;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}